<template>
  <div class="map-with-search">
    <div class="map-with-search__search-box">
      <button class="map-with-search__back-button" type="button" @click="$emit('hide-mobile-map')">
        <TheArrowHorizIcon />
      </button>
      <button class="map-with-search__search-btn" type="button" @click="handleToggleSearchModal">Search</button>
      <button class="map-with-search__filter-btn" type="button" @click="handleOpenFilter">
        <TheFilterIcon />
      </button>
    </div>

    <div class="map-with-search__main-content">
      <div class="map-with-search__map">
        <MglMap :access-token="mapAccessToken" :map-style="mapStyle" @click="handleClickOnMap" @load="onMapLoaded">
          <MglGeojsonLayer
            :layerId="mapCurrentPositionLayer.id"
            :layer="mapCurrentPositionLayer"
            :sourceId="mapCurrentPositionSource.id"
            :source="mapCurrentPositionSource.source"
          />
          <MglGeojsonLayer
            :layerId="mapRouteDestinationLayer.id"
            :layer="mapRouteDestinationLayer"
            :sourceId="mapRouteDestinationSource.id"
            :source="mapRouteDestinationSource.source"
          />
          <MglMarker
            v-for="marker in mapMarkers"
            :key="`marker_${marker.key}`"
            :coordinates="[marker.lng, marker.lat]"
            :offset="[0, -14]"
            @click="handleClickOnMapMarker($event, marker)"
          >
            <MapMarkerEntityIcon slot="marker" :active-id="markerActiveKey" :id="marker.key" :type="marker.type" />
          </MglMarker>
        </MglMap>
      </div>
    </div>

    <AppDataFilterMain
      v-model="filterValues"
      :filter-groups="filterGroups"
      :init-open="isAppDataFilterOpened"
      :is-mobile="isMobileScreen"
      @input="handleChangeFilterValues"
      @open="handleOpenFilter"
      @close="handleCloseFilter"
    />

    <Transition name="slide-up">
      <AppSearch
        v-if="showSearch"
        class="map-with-search__search-component"
        :init-destination-id="destinationId"
        @close="handleToggleSearchModal(false)"
      />
    </Transition>

    <Transition name="slide-up-full">
      <MapWithSearchEntityDetails
        v-if="showDetailsModal"
        :entity="entityDetails"
        @get-map-direction="drawMapDirection(entityDetails)"
      />
    </Transition>

    <MapDrawDirectionErrorModal v-model="drawMapDirectionErrorMsg" />
  </div>
</template>

<script>
import { MglGeojsonLayer, MglMap, MglMarker } from 'vue-mapbox';
import { EntityTypes } from '@/helpers/entityType';
import { validateAndGetCoordinates } from '@/helpers/GeolocationHelper';
import {
  getMapBounds,
  getMapCurrentPositionSource,
  getMapDirectionFromCurrentPosition,
  getMapRouteDestinationSource,
  MAP_ACCESS_TOKEN,
  MAP_LAYER_CURRENT_POSITION,
  MAP_LAYER_ROUTE_DESTINATION,
  MAP_STYLE_URL,
} from '@/helpers/mapboxHelper';
import DestinationEntitiesMapDataFilter from '@/helpers/data-filter/DestinationEntitiesMapDataFilter';

import AppDataFilterMain from '@/components/data-filter/AppDataFilterMain';
import AppSearch from '@/components/partials/search/AppSearch';
import MapWithSearchEntityDetails from '@/components/partials/MapWithSearchEntityDetails';
import MapMarkerEntityIcon from '@/components/partials/MapMarkerEntityIcon';
import MapDrawDirectionErrorModal from '@/components/partials/MapDrawDirectionErrorModal';
import TheArrowHorizIcon from '@/components/icons/TheArrowHorizIcon.vue';
import TheFilterIcon from '@/components/icons/TheFilterIcon.vue';

export default {
  name: 'MapWithSearch',
  components: {
    TheFilterIcon,
    TheArrowHorizIcon,
    MapDrawDirectionErrorModal,
    MapMarkerEntityIcon,
    AppDataFilterMain,
    AppSearch,
    MapWithSearchEntityDetails,
    MglMap,
    MglMarker,
    MglGeojsonLayer,
  },
  props: {
    destinationId: {
      type: Number,
      required: true,
    },
    initEntities: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      entityDetails: {},
      filterGroups: [],
      filterValues: {
        types: [],
      },
      isAppDataFilterOpened: false,
      showDetailsModal: false,
      showSearch: false,

      mapAccessToken: MAP_ACCESS_TOKEN,
      mapStyle: MAP_STYLE_URL,
      markerActiveKey: null,

      mapCurrentPositionLayer: MAP_LAYER_CURRENT_POSITION,
      mapRouteDestinationLayer: MAP_LAYER_ROUTE_DESTINATION,
      mapCurrentPositionSourceCoords: [],
      mapRouteDestinationSourceCoords: [],
      drawMapDirectionErrorMsg: '',
    };
  },
  computed: {
    entities() {
      return this.initEntities.filter((e) => validateAndGetCoordinates(e) !== false);
    },
    filteredEntities() {
      return this.entities.filter(
        (e) => !this.filterValues.types.length || this.filterValues.types.includes(e.__typename)
      );
    },
    isMobileScreen() {
      return !!this.$store.state.isMobileScreen;
    },
    mapMarkers() {
      return this.filteredEntities
        .map((e) => {
          const coordinates = validateAndGetCoordinates(e);
          if (coordinates === false) {
            return;
          }
          return {
            id: e.id,
            key: `${e.__typename}_${e.id}`,
            __typename: e.__typename,
            type: e.__typename,
            slug: e.slug,
            title: e.title,
            lat: coordinates.latitude,
            lng: coordinates.longitude,
            destination: e.destination,
          };
        })
        .filter((m) => m);
    },
    mapCurrentPositionSource() {
      return getMapCurrentPositionSource(this.mapCurrentPositionSourceCoords);
    },
    mapRouteDestinationSource() {
      return getMapRouteDestinationSource(this.mapRouteDestinationSourceCoords);
    },
  },
  created() {
    this.getFilterData();
  },
  methods: {
    getFilterData() {
      const allTypes = [
        { id: EntityTypes.art_fair, title: 'Art Fair' },
        { id: EntityTypes.art_space, title: 'Art Space' },
        { id: EntityTypes.event, title: 'Event' },
        { id: EntityTypes.exhibition, title: 'Exhibition' },
        { id: EntityTypes.historical_site, title: 'Historical site' },
      ];
      const typeKeys = [...new Set(this.entities.map((e) => e.__typename))];
      const types = allTypes
        .filter((t) => typeKeys.indexOf(t.id) !== -1)
        .sort((a, b) => {
          if (a.title < b.title) {
            return -1;
          }
          if (a.title > b.title) {
            return 1;
          }
          return 0;
        });

      this.filterGroups = DestinationEntitiesMapDataFilter.prepareFilterGroups({ types });
    },
    handleOpenFilter() {
      this.isAppDataFilterOpened = true;
    },
    handleCloseFilter() {
      this.$store.dispatch('disableScroll');
      this.isAppDataFilterOpened = false;
    },
    handleChangeFilterValues() {
      this.closeDetailsModal();
      this.clearMapDirection();
    },

    handleToggleSearchModal(isShow) {
      this.toggleSearchModal(isShow);
      this.closeDetailsModal();
    },
    toggleSearchModal(isShow) {
      this.showSearch = typeof isShow !== 'boolean' ? !this.showSearch : !!isShow;
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.entityDetails = {};
      this.markerActiveKey = null;
    },

    onMapLoaded({ map }) {
      map.fitBounds(getMapBounds(this.mapMarkers), {
        padding: { top: 120, bottom: 80, left: 80, right: 80 },
      });
    },
    handleClickOnMap({ mapboxEvent }) {
      if (!mapboxEvent.originalEvent.target.closest('.map-marker')) {
        this.closeDetailsModal();
      }
    },
    handleClickOnMapMarker(data, marker) {
      this.markerActiveKey = marker.key;
      this.entityDetails = marker;
      this.showDetailsModal = true;
    },
    drawMapDirection(destinationCoordinates) {
      getMapDirectionFromCurrentPosition(destinationCoordinates)
        .then(({ currentPosition, routeCoordinates }) => {
          this.mapRouteDestinationSourceCoords = routeCoordinates;
          if (
            !this.mapCurrentPositionSourceCoords.length ||
            (currentPosition[0] !== this.mapCurrentPositionSourceCoords[0] &&
              currentPosition[1] !== this.mapCurrentPositionSourceCoords[1])
          ) {
            this.mapCurrentPositionSourceCoords = currentPosition;
          }
        })
        .catch((err) => {
          this.clearMapDirection();
          this.drawMapDirectionErrorMsg = err.message;
        });
    },
    clearMapDirection() {
      this.mapCurrentPositionSourceCoords = [];
      this.mapRouteDestinationSourceCoords = [];
    },
  },
};
</script>
